import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getUserProfile, updateUserProfile, sendResetVerificationEmail } from "../../../redux/auth/action/userProfileActions";
import { getUserTickets } from "../../../redux/auth/action/userTicketActions";
import { getWishlist } from "../../../redux/auth/action/fetchWishlist";
import { useNavigate } from "react-router-dom";
import { 
  FiEdit2, 
  FiCamera, 
  FiMail, 
  FiPhone, 
  FiCalendar,
  FiMapPin,
  FiSettings,
  FiShield
} from "react-icons/fi";
import { 
  VscUnverified, 
  VscVerified 
} from "react-icons/vsc";
import { 
  FaTicketAlt, 
  FaHeart, 
  FaCalendarCheck,
  FaUser,
  FaCrown
} from "react-icons/fa";
import userImage from "../../../assets/images/user.png";
import ProfileUpdateModal from "./ProfileUpdateModal";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Profile = () => {
  const dispatch = useDispatch();
  const profileState = useSelector((state) => state.profile);
  const { tickets } = useSelector((state) => state.ticketList);
  const { wishlist } = useSelector((state) => state.wishlist);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCooldown, setIsCooldown] = useState(false);
  const resetVerificationState = useSelector((state) => state.resetVerification);
  const [hasDisplayedToast, setHasDisplayedToast] = useState(true);
  const navigate = useNavigate();

  // Calculate stats from real data
  const calculateStats = () => {
    // Total tickets purchased (completed payments only)
    const completedTickets = (tickets || []).filter(
      ticket => ticket.payment_status === 'completed'
    );
    const totalTickets = completedTickets.reduce((sum, ticket) => {
      return sum + (ticket.ticket_quantity || 1);
    }, 0);

    // Events attended (unique events from completed tickets)
    const uniqueEventIds = new Set();
    completedTickets.forEach(ticket => {
      if (ticket.event) {
        uniqueEventIds.add(ticket.event);
      }
    });
    const eventsAttended = uniqueEventIds.size;

    // Saved events (wishlist count)
    const savedEvents = (wishlist || []).length;

    return {
      totalTickets,
      eventsAttended,
      savedEvents
    };
  };

  const stats = calculateStats();

  // Fetch profile data and related information
  useEffect(() => {
    dispatch(getUserProfile());
    dispatch(getUserTickets());
    dispatch(getWishlist());

    let timer;
    if (isCooldown) {
      timer = setTimeout(() => {
        setIsCooldown(false);
      }, 10000);
    }
    return () => clearTimeout(timer);
  }, [dispatch, isCooldown]);

  // Handle verification email toast
  useEffect(() => {
    if (
      resetVerificationState.data &&
      !resetVerificationState.loading &&
      !hasDisplayedToast
    ) {
      if (!toast.isActive(1, "profileComponent")) {
        toast.success(
          "Verification email sent successfully!", {
          toastId: 1
        }
        );
      }
      setHasDisplayedToast(true);
    }
    if (
      resetVerificationState.error &&
      !hasDisplayedToast
    ) {
      if (!toast.isActive(2, "profileComponent")) {
        toast.error(
          "Failed to send verification email. Please try again.", {
          toastId: 2
        }
        );
      }
      setHasDisplayedToast(true);
    }
  }, [
    resetVerificationState.data,
    resetVerificationState.error,
    resetVerificationState.loading,
    hasDisplayedToast,
  ]);

  if (!localStorage.getItem("access") && !sessionStorage.getItem("access")) {
    navigate("/login");
  }

  if (!profileState.profile) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sunday_primary"></div>
      </div>
    );
  }

  const {
    full_name,
    email,
    phone_number,
    profile_picture,
    is_verified
  } = profileState.profile || {};

  const initialValues = {
    full_name,
    email,
    phone_number: phone_number ?? "",
    password: "",
    confirm_password: "",
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  // Handle image file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const formData = new FormData();
      formData.append('full_name', full_name);
      formData.append('email', email);
      formData.append('phone_number', phone_number);
      formData.append('profile_picture', file);

      dispatch(updateUserProfile(formData));
    }
  };

  // Function to resend verification email
  const handleResendVerificationEmail = () => {
    dispatch(sendResetVerificationEmail(email));
    setHasDisplayedToast(false);
    setIsCooldown(true);
  };

  const getVerificationBadge = () => {
    if (is_verified) {
      return (
        <div className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-medium">
          <VscVerified size={16} />
          <span>Verified</span>
        </div>
      );
    } else {
      return (
        <button
          onClick={handleResendVerificationEmail}
          disabled={isCooldown}
          className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium transition-all ${
            isCooldown
              ? 'bg-gray-100 text-gray-500 cursor-not-allowed'
              : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
          }`}
        >
          <VscUnverified size={16} />
          <span>{isCooldown ? 'Wait 10s' : 'Verify Email'}</span>
        </button>
      );
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6">
      <ToastContainer position="top-right" containerId="profileComponent" autoClose={3000} />

      {/* Profile Header Card - Consistent Typography & Colors */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Profile Info - Consistent Design */}
        <div className="p-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
            {/* Profile Picture */}
            <div className="relative flex-shrink-0">
              <img
                src={profile_picture || userImage}
                alt="Profile"
                className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-gray-100 shadow-lg object-cover bg-white"
              />
              <label 
                htmlFor="profile-picture-upload" 
                className="absolute bottom-1 right-1 sm:bottom-2 sm:right-2 bg-sunday_primary text-white p-2 rounded-full cursor-pointer shadow-lg hover:bg-sunday_secondary transition-colors"
              >
                <FiCamera size={14} />
                <input
                  id="profile-picture-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileChange}
                  className="hidden"
                />
              </label>
            </div>

            {/* Profile Details */}
            <div className="flex-1 min-w-0 w-full">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="min-w-0 flex-1">
                  {/* Name and Verification */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 truncate">
                      {full_name || "User"}
                    </h1>
                    {getVerificationBadge()}
                  </div>

                  {/* Contact Information */}
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FiMail size={16} className="text-blue-600" />
                      </div>
                      <span className="text-gray-600 truncate min-w-0 text-sm font-medium">{email}</span>
                    </div>
                    
                    {phone_number && (
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-green-50 rounded-lg flex items-center justify-center flex-shrink-0">
                          <FiPhone size={16} className="text-green-600" />
                        </div>
                        <span className="text-gray-600 truncate min-w-0 text-sm font-medium">{phone_number}</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FiCalendar size={16} className="text-purple-600" />
                      </div>
                      <span className="text-gray-600 text-sm font-medium">Member since 2024</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0">
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 bg-sunday_primary text-white rounded-lg hover:bg-sunday_secondary transition-colors font-medium text-sm shadow-sm"
                  >
                    <FiEdit2 size={16} />
                    Edit Profile
                  </button>
                  <button
                    onClick={() => navigate("/forgotpassword")}
                    className="flex items-center justify-center gap-2 px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm"
                  >
                    <FiShield size={16} />
                    Security
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Cards - Real Data */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
          <div className="w-12 h-12 bg-sunday_primary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
            <FaTicketAlt className="text-sunday_primary" size={20} />
          </div>
          <h3 className="text-2xl font-bold text-gray-800">{stats.totalTickets}</h3>
          <p className="text-gray-600 text-sm font-medium">Total Tickets</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
          <div className="w-12 h-12 bg-sunday_secondary/10 rounded-xl flex items-center justify-center mx-auto mb-3">
            <FaCalendarCheck className="text-sunday_secondary" size={20} />
          </div>
          <h3 className="text-2xl font-bold text-gray-800">{stats.eventsAttended}</h3>
          <p className="text-gray-600 text-sm font-medium">Events Attended</p>
        </div>

        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center mx-auto mb-3">
            <FaHeart className="text-red-600" size={20} />
          </div>
          <h3 className="text-2xl font-bold text-gray-800">{stats.savedEvents}</h3>
          <p className="text-gray-600 text-sm font-medium">Saved Events</p>
        </div>
      </div>

      {/* Quick Actions - Consistent Design */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-6">Quick Actions</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button 
            onClick={() => navigate('/dashboard?section=purchase-history')}
            className="group p-3 rounded-xl border border-gray-200 hover:border-sunday_primary hover:bg-sunday_primary/5 transition-all"
          >
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="w-12 h-12 bg-sunday_primary/10 rounded-xl flex items-center justify-center group-hover:bg-sunday_primary/20 transition-colors">
                <FaTicketAlt className="text-sunday_primary" size={18} />
              </div>
              <div>
                <p className="font-medium text-gray-800 text-xs leading-tight">My Tickets</p>
                <p className="text-xs text-gray-600 leading-tight mt-1">View purchases</p>
              </div>
            </div>
          </button>

          <button 
            onClick={() => navigate('/dashboard?section=wishlist')}
            className="group p-3 rounded-xl border border-gray-200 hover:border-sunday_primary hover:bg-sunday_primary/5 transition-all"
          >
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center group-hover:bg-red-200 transition-colors">
                <FaHeart className="text-red-600" size={18} />
              </div>
              <div>
                <p className="font-medium text-gray-800 text-xs leading-tight">Wishlist</p>
                <p className="text-xs text-gray-600 leading-tight mt-1">Saved events</p>
              </div>
            </div>
          </button>

          <button 
            onClick={() => navigate('/dashboard?section=settings')}
            className="group p-3 rounded-xl border border-gray-200 hover:border-sunday_primary hover:bg-sunday_primary/5 transition-all"
          >
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="w-12 h-12 bg-gray-100 rounded-xl flex items-center justify-center group-hover:bg-gray-200 transition-colors">
                <FiSettings className="text-gray-600" size={18} />
              </div>
              <div>
                <p className="font-medium text-gray-800 text-xs leading-tight">Settings</p>
                <p className="text-xs text-gray-600 leading-tight mt-1">Preferences</p>
              </div>
            </div>
          </button>

          <button 
            onClick={() => window.open('https://organizer.eventfullsunday.co.uk/', '_blank')}
            className="group p-3 rounded-xl border border-gray-200 hover:border-sunday_primary hover:bg-sunday_primary/5 transition-all"
          >
            <div className="flex flex-col items-center text-center space-y-2">
              <div className="w-12 h-12 bg-sunday_secondary/10 rounded-xl flex items-center justify-center group-hover:bg-sunday_secondary/20 transition-colors">
                <FaCrown className="text-sunday_secondary" size={18} />
              </div>
              <div>
                <p className="font-medium text-gray-800 text-xs leading-tight">Create Event</p>
                <p className="text-xs text-gray-600 leading-tight mt-1">Become organizer</p>
              </div>
            </div>
          </button>
        </div>
      </div>

      {/* Account Information - Consistent Design */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Account Settings</h2>
        <div className="space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
                <FiMail className="text-blue-600" size={18} />
              </div>
              <div>
                <p className="font-medium text-gray-800">Email Verification</p>
                <p className="text-sm text-gray-600">Manage your email verification status</p>
              </div>
            </div>
            {getVerificationBadge()}
          </div>

          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-50 rounded-lg flex items-center justify-center">
                <FiPhone className="text-green-600" size={18} />
              </div>
              <div>
                <p className="font-medium text-gray-800">Phone Number</p>
                <p className="text-sm text-gray-600">
                  {phone_number ? 'Update your phone number' : 'Add a phone number for security'}
                </p>
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="text-sunday_primary hover:text-sunday_secondary text-sm font-medium px-3 py-1 rounded-lg hover:bg-sunday_primary/10 transition-colors"
            >
              {phone_number ? 'Update' : 'Add'}
            </button>
          </div>

          <div className="flex items-center justify-between py-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
                <FiShield className="text-purple-600" size={18} />
              </div>
              <div>
                <p className="font-medium text-gray-800">Password & Security</p>
                <p className="text-sm text-gray-600">Change password and security settings</p>
              </div>
            </div>
            <button
              onClick={() => navigate("/forgotpassword")}
              className="text-sunday_primary hover:text-sunday_secondary text-sm font-medium px-3 py-1 rounded-lg hover:bg-sunday_primary/10 transition-colors"
            >
              Manage
            </button>
          </div>
        </div>
      </div>

      {/* Profile Update Modal */}
      <ProfileUpdateModal
        isOpen={isModalOpen}
        onRequestClose={handleCloseModal}
        initialValues={initialValues}
      />
    </div>
  );
};

export default Profile;